import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';

import { DirectionService } from '../../direction.service';
import { DialogService } from '../dialog.service';
import { PHOrdersService } from '../../super-management/ph-manigment/ph-orders-service';
import { BranchesService } from '../../super-management/branch/branches.service';
import { DataSharingService } from '../../main-section/data-shering-service/data-sharing.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';

export interface CreditOrderDialogData {
  order: any;
  mode: 'credit' | 'points';
  orderType: 'ph' | 'plotter' | 'express';
  /** For plotter/express: service name passed to cancel API (e.g. from printer: printingService). */
  service?: string;
}

@Component({
  selector: 'app-credit-order',
  templateUrl: './credit-order.component.html',
  styleUrls: ['./credit-order.component.css'],
  host: { class: 'fill-screen-modal-phone' }
})
export class CreditOrderComponent {
  isRTL = true;
  isDarkMode = false;
  isLoading = false;

  constructor(
    private directionService: DirectionService,
    private dialogService: DialogService,
    private phOrdersService: PHOrdersService,
    private branchesService: BranchesService,
    private dataSharingService: DataSharingService,
    private dialogRef: MatDialogRef<CreditOrderComponent>,
    private snackBar: MatSnackBar,
    private translate: TranslateService,
    @Inject(MAT_DIALOG_DATA) public data: CreditOrderDialogData
  ) {
    this.directionService.direction$.subscribe(d => (this.isRTL = d === 'rtl'));
    this.directionService.isDarkMode$.subscribe(d => (this.isDarkMode = d));
  }

  get order(): any {
    return this.data?.order || {};
  }

  get isCreditMode(): boolean {
    return this.data?.mode === 'credit';
  }

  get isPointsMode(): boolean {
    return this.data?.mode === 'points';
  }

  get orderIdPrefix(): string {
    const id = this.order?._id;
    return id ? String(id).slice(0, 8) : '';
  }

  /** סכום תשלום באשראי (שקלים) – תמיד מ-ccCost כשקיים, אחרת fallback לפי סוג הזמנה. */
  getAmount(): number {
    const o = this.order;
    if (o?.ccCost != null && !isNaN(Number(o.ccCost))) {
      return Number(o.ccCost);
    }
    if (this.data?.orderType === 'ph') {
      const q = o?.lastPriceQuote || (o?.priceQuotes && o.priceQuotes[o.priceQuotes.length - 1]);
      return q?.orderPrice != null ? Number(q.orderPrice) : 0;
    }
    return (o?.totalCost != null ? Number(o.totalCost) : 0) || 0;
  }

  /** Points used – for PH from lastPriceQuote or order; for plotter/express from pointsCost. */
  getPoints(): number {
    const o = this.order;
    if (this.data?.orderType === 'ph') {
      const q = o?.lastPriceQuote || (o?.priceQuotes && o.priceQuotes[o.priceQuotes.length - 1]);
      return (q?.pointsUsed != null ? Number(q.pointsUsed) : 0) || (o?.pointsUsed != null ? Number(o.pointsUsed) : 0) || 0;
    }
    return (o?.pointsCost != null ? Number(o.pointsCost) : 0) || 0;
  }

  /** סכום תשלום באשראי + סכום תשלום בנקודות – להצגה במצב זיכוי בנקודות. */
  getTotalCreditAndPoints(): number {
    return this.getAmount() + this.getPoints();
  }

  roundAmount(value: number): string {
    if (value == null || isNaN(value)) return '0';
    return Number(value).toFixed(2);
  }

  closeDialog(): void {
    this.dialogService.onCloseCreditOrderDialog();
    this.dialogRef.close();
  }

  /** בקשת תשלום SU היא OrderExpress בלי branch – לא plotter. */
  private resolveRefundService(): 'express' | 'plotter' {
    if (this.data?.service === 'express' || this.data?.service === 'plotter') {
      return this.data.service;
    }
    const o = this.order;
    if (
      o?.branchID?.is_express ||
      o?.is_only_fix_products_in_order ||
      o?.fix_products_order?.isEmptyOrder === true
    ) {
      return 'express';
    }
    return 'plotter';
  }

  performRefund(): void {
    const orderId = this.order?._id;
    if (!orderId) return;
    this.isLoading = true;

    if (this.data?.orderType === 'ph') {
      if (this.data.mode === 'credit') {
        this.phOrdersService.refundCredit(orderId).subscribe({
          next: () => {
            this.dataSharingService.notifyPendingOrderDecrement();
            this.dialogRef.close(true);
          },
          error: () => {
            this.isLoading = false;
            this.snackBar.open(this.translate.instant('chat.error-cancel-price-quote'), '', { duration: 4000, verticalPosition: 'top' });
          }
        });
      } else {
        this.phOrdersService.refundPoints(orderId).subscribe({
          next: () => {
            this.dataSharingService.notifyPendingOrderDecrement();
            this.dialogRef.close(true);
          },
          error: () => {
            this.isLoading = false;
            this.snackBar.open(this.translate.instant('chat.error-cancel-price-quote'), '', { duration: 4000, verticalPosition: 'top' });
          }
        });
      }
      return;
    }

    const action = this.data?.mode === 'credit' ? 'REFUNDED' : 'REFUNDED_POINTS';
    const service = this.resolveRefundService();
    this.branchesService.onCancelOrder(service, action, orderId).subscribe({
      next: () => {
        this.dataSharingService.notifyPendingOrderDecrement();
        this.dialogRef.close(true);
      },
      error: () => {
        this.isLoading = false;
        this.snackBar.open(this.translate.instant('su-management.cancel-order.error'), '', {
          duration: 4000,
          verticalPosition: 'top'
        });
      }
    });
  }
}
